import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { useLlmModels } from "@/hooks/useLlmModels";
import type { LlmModelId } from "@smartmed/llm-models";
import { MultiSelect, type Option } from "@/components/ui/multi-select";
import dayjs from "dayjs";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { getPatientEncounterLabel } from "@/test-data/patientEncounterLabel";
import RegressionsPopover from "./RegressionsPopover";
import { TriggerPatientEncounterRun } from "./TriggerPatientEncounterRun";
import { TriggerRunDialog } from "./TriggerRunDialog";
import { useTestRunsWithRealtime } from "./useTestRunsWithRealtime";

interface TestRun {
  id: string;
  run_start_time: string | null;
  run_end_time: string | null;
  success: boolean | null;
  llm_model_id: LlmModelId | null;
}

const FILTERS = [
  { label: "5m", value: 5 * 60 },
  { label: "30m", value: 30 * 60 },
  { label: "1h", value: 60 * 60 },
  { label: "12h", value: 12 * 60 * 60 },
  { label: "24h", value: 24 * 60 * 60 },
];

const TestRuns = () => {
  const navigate = useNavigate();
  const [isTriggerPatientEncounterRunOpen, setIsTriggerPatientEncounterRunOpen] = useState(false);
  const [isTriggerRunDialogOpen, setIsTriggerRunDialogOpen] = useState(false);
  const [customStartDatePickerOpen, setCustomStartDatePickerOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const startSinceParam = searchParams.get("startSince");
  const demoModeParam = searchParams.get("demoMode");
  const modelParam = searchParams.get("model");
  const patientParam = searchParams.get("patient");
  const encounterParam = searchParams.get("encounter");
  const patientsParam = searchParams.get("patients");
  const encountersParam = searchParams.get("encounters");

  const [filterStartDate, setFilterStartDate] = useState<Date | null>(
    startSinceParam ? new Date(startSinceParam) : dayjs.utc().subtract(24, "hour").toDate(),
  );
  const [showOnlySuccessful, setShowOnlySuccessful] = useState(demoModeParam === "true");
  const [selectedModelId, setSelectedModelId] = useState<string | null>(modelParam || null);
  const [selectedPatientIds, setSelectedPatientIds] = useState<string[]>(
    patientsParam?.split(",").filter(Boolean) || (patientParam ? [patientParam] : []),
  );
  const [selectedEncounterIds, setSelectedEncounterIds] = useState<string[]>(
    encountersParam?.split(",").filter(Boolean) || (encounterParam ? [encounterParam] : []),
  );
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);

  const { data: llmModels } = useLlmModels();
  const { runs, isLoading } = useTestRunsWithRealtime({
    filterStartTime: filterStartDate,
  });

  // Apply all filters to the runs
  const filteredRuns = runs?.filter((run) => {
    // Success filter
    if (showOnlySuccessful && run.success !== true) {
      return false;
    }

    // Model filter
    if (selectedModelId && run.llm_model_id !== selectedModelId) {
      return false;
    }

    // Patient filter (multi)
    if (
      selectedPatientIds.length > 0 &&
      !selectedPatientIds.includes(run.encounter?.patient_encounters?.patient?.id ?? "")
    ) {
      return false;
    }

    // Encounter filter (multi)
    if (
      selectedEncounterIds.length > 0 &&
      !selectedEncounterIds.includes(run.encounter?.patient_encounters?.id ?? "")
    ) {
      return false;
    }

    return true;
  });

  // Helper to update both state and URL param
  const updateFilterStartDate = (date: Date | null) => {
    setFilterStartDate(date);
    if (date) {
      setSearchParams({
        ...Object.fromEntries(searchParams.entries()),
        startSince: date.toISOString(),
      });
    } else {
      const params = Object.fromEntries(searchParams.entries());
      delete params.startSince;
      setSearchParams(params);
    }
  };

  // Helper to update demo mode state and URL param
  const updateDemoMode = (enabled: boolean) => {
    setShowOnlySuccessful(enabled);
    if (enabled) {
      setSearchParams({
        ...Object.fromEntries(searchParams.entries()),
        demoMode: "true",
      });
    } else {
      const params = Object.fromEntries(searchParams.entries());
      delete params.demoMode;
      setSearchParams(params);
    }
  };

  // Helper to update model filter and URL param
  const updateModelFilter = (modelId: string | null) => {
    setSelectedModelId(modelId);
    const params = Object.fromEntries(searchParams.entries());
    if (modelId) {
      params.model = modelId;
    } else {
      delete params.model;
    }
    setSearchParams(params);
  };

  // Helpers to update multi-select filters and URL params
  const updatePatientsFilter = (patientIds: string[]) => {
    setSelectedPatientIds(patientIds);
    const params = Object.fromEntries(searchParams.entries());
    if (patientIds.length > 0) {
      params.patients = patientIds.join(",");
      delete params.patient; // clean old single param
    } else {
      delete params.patients;
      delete params.patient;
    }
    setSearchParams(params);
  };

  const updateEncountersFilter = (encounterIds: string[]) => {
    setSelectedEncounterIds(encounterIds);
    const params = Object.fromEntries(searchParams.entries());
    if (encounterIds.length > 0) {
      params.encounters = encounterIds.join(",");
      delete params.encounter; // clean old single param
    } else {
      delete params.encounters;
      delete params.encounter;
    }
    setSearchParams(params);
  };

  // Keep state in sync if user navigates browser history
  React.useEffect(() => {
    const param = searchParams.get("startSince");
    if (param) {
      const date = new Date(param);
      if (!filterStartDate || filterStartDate.getTime() !== date.getTime()) {
        setFilterStartDate(date);
      }
    }
    // Note: We don't clear filterStartDate when param is missing because
    // the initial state should be preserved unless explicitly cleared by user

    // Sync demo mode state with URL param
    const demoParam = searchParams.get("demoMode");
    const shouldBeInDemoMode = demoParam === "true";
    if (showOnlySuccessful !== shouldBeInDemoMode) {
      setShowOnlySuccessful(shouldBeInDemoMode);
    }

    // Sync model filter state with URL param
    const modelParam = searchParams.get("model");
    if (selectedModelId !== modelParam) {
      setSelectedModelId(modelParam);
    }

    // Sync patient filter state with URL params (support single or multi)
    const patientParam = searchParams.get("patient");
    const patientsParam = searchParams.get("patients");
    const nextPatientIds = patientsParam
      ? patientsParam.split(",").filter(Boolean)
      : patientParam
        ? [patientParam]
        : [];
    if (JSON.stringify(selectedPatientIds) !== JSON.stringify(nextPatientIds)) {
      setSelectedPatientIds(nextPatientIds);
    }

    // Sync encounter filter state with URL params (support single or multi)
    const encounterParam = searchParams.get("encounter");
    const encountersParam = searchParams.get("encounters");
    const nextEncounterIds = encountersParam
      ? encountersParam.split(",").filter(Boolean)
      : encounterParam
        ? [encounterParam]
        : [];
    if (JSON.stringify(selectedEncounterIds) !== JSON.stringify(nextEncounterIds)) {
      setSelectedEncounterIds(nextEncounterIds);
    }
  }, [filterStartDate, searchParams, selectedEncounterIds, selectedModelId, selectedPatientIds, showOnlySuccessful]);

  // Get unique models, patients, and encounters from runs for filter options
  const availableModels = React.useMemo(() => {
    if (!runs || !llmModels) return [];
    const uniqueModelIds = [...new Set(runs.map((run) => run.llm_model_id))];
    return uniqueModelIds
      .map((modelId) => {
        const model = llmModels.find((m) => m.modelId === modelId);
        return model ? { id: modelId, name: model.name } : null;
      })
      .filter((m): m is NonNullable<typeof m> => m !== null)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [runs, llmModels]);

  const availablePatients = React.useMemo(() => {
    if (!runs) return [];
    const uniquePatients = runs
      .filter((run) => run.encounter?.patient_encounters?.patient?.id)
      .map((run) => run.encounter!.patient_encounters!.patient!.id)
      .filter((id, index, arr) => arr.indexOf(id) === index)
      .sort();
    return uniquePatients;
  }, [runs]);

  const availableEncounters = React.useMemo(() => {
    if (!runs) return [];
    const uniqueEncounters = new Map<
      string,
      NonNullable<NonNullable<(typeof runs)[number]["encounter"]>["patient_encounters"]>
    >();

    for (const run of runs) {
      const encounter = run.encounter?.patient_encounters;
      if (encounter && !uniqueEncounters.has(encounter.id)) {
        uniqueEncounters.set(encounter.id, encounter);
      }
    }

    return [...uniqueEncounters.values()].sort((a, b) =>
      getPatientEncounterLabel(a).localeCompare(getPatientEncounterLabel(b)),
    );
  }, [runs]);

  const patientOptions: Option<string>[] = React.useMemo(
    () => availablePatients.map((p) => ({ value: p ?? "", label: p ?? "" })),
    [availablePatients],
  );
  const encounterOptions: Option<string>[] = React.useMemo(
    () =>
      availableEncounters.map((encounter) => ({
        value: encounter.id,
        label: getPatientEncounterLabel(encounter),
      })),
    [availableEncounters],
  );

  const hasAppliedFilters = Boolean(
    showOnlySuccessful ||
      selectedModelId ||
      selectedPatientIds.length > 0 ||
      selectedEncounterIds.length > 0 ||
      filterStartDate !== null,
  );

  // Helper to get run number for a given run and date
  const getRunNumberForDate = (runs: TestRun[], run: TestRun) => {
    const runDate = (run.run_start_time ?? "").slice(0, 10);
    // Filter runs for this date, ordered ascending (oldest first)
    const runsForDate = runs.filter((r) => (r.run_start_time ?? "").slice(0, 10) === runDate);
    const sortedRuns = runsForDate.sort((a, b) => (a.run_start_time ?? "").localeCompare(b.run_start_time ?? ""));
    const index = sortedRuns.findIndex((r) => r.id === run.id);
    return index + 1;
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">Test Runs</h1>
              <button
                onClick={() => updateDemoMode(!showOnlySuccessful)}
                className="text-2xl hover:scale-110 transition-transform cursor-pointer"
                title={showOnlySuccessful ? "Show all runs" : "Show successful runs only (Demo mode)"}
              >
                {showOnlySuccessful ? "✅" : "🚧"}
              </button>
            </div>
            <p className="text-muted-foreground">View and analyze classification test runs.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setIsTriggerPatientEncounterRunOpen(true)}>Trigger Patient Encounter Run</Button>
            <Button onClick={() => setIsTriggerRunDialogOpen(true)}>Trigger New Stage Run</Button>
            <RegressionsPopover />
          </div>
          <TriggerRunDialog
            open={isTriggerRunDialogOpen}
            onClose={() => setIsTriggerRunDialogOpen(false)}
            onRunsTriggered={() => {}}
          />
          <TriggerPatientEncounterRun
            open={isTriggerPatientEncounterRunOpen}
            onClose={() => setIsTriggerPatientEncounterRunOpen(false)}
          />
        </div>

        {/* Top-level Filter button and indicator */}
        {showOnlySuccessful ? null : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setIsFilterDialogOpen(true)}>
                Filter
              </Button>
              {hasAppliedFilters ? (
                <span className="text-sm text-muted-foreground">Filters applied</span>
              ) : (
                <span className="text-sm text-muted-foreground">No filters</span>
              )}
            </div>
          </div>
        )}

        {/* Filter Dialog */}
        <Dialog open={isFilterDialogOpen} onOpenChange={setIsFilterDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Filters</DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Model:</label>
                <Select
                  value={selectedModelId || "all"}
                  onValueChange={(value) => updateModelFilter(value === "all" ? null : value)}
                >
                  <SelectTrigger className="w-[240px]">
                    <SelectValue placeholder="All models" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All models</SelectItem>
                    {availableModels.map((model) => (
                      <SelectItem key={model.id ?? ""} value={model.id ?? ""}>
                        {model.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Patients</label>
                <MultiSelect
                  options={patientOptions}
                  value={selectedPatientIds.map((p) => ({
                    value: p,
                    label: p,
                  }))}
                  onChange={(items) => updatePatientsFilter(items.map((i) => i.value))}
                  placeholder="All patients"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Encounters</label>
                <MultiSelect
                  options={encounterOptions}
                  value={selectedEncounterIds.map((e) => ({
                    value: e,
                    label: encounterOptions.find((option) => option.value === e)?.label ?? e,
                  }))}
                  onChange={(items) => updateEncountersFilter(items.map((i) => i.value))}
                  placeholder="All encounters"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Started Since</label>
                <div className="flex flex-wrap gap-2 items-center">
                  {FILTERS.map((f) => {
                    const filterDate = new Date(Date.now() - f.value * 1000);
                    return (
                      <Button
                        key={f.value}
                        variant={
                          filterStartDate && Math.abs(filterStartDate.getTime() - filterDate.getTime()) < 1000 * 60
                            ? "default"
                            : "outline"
                        }
                        onClick={() => {
                          updateFilterStartDate(filterDate);
                          setCustomStartDatePickerOpen(false);
                        }}
                      >
                        {f.label}
                      </Button>
                    );
                  })}
                  <Popover open={customStartDatePickerOpen} onOpenChange={setCustomStartDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant={(() => {
                          if (customStartDatePickerOpen) return "default";
                          if (!filterStartDate) return "outline";
                          const isPreset = FILTERS.some((f) => {
                            const d = new Date(Date.now() - f.value * 1000);
                            return Math.abs(filterStartDate.getTime() - d.getTime()) < 1000 * 60;
                          });
                          return isPreset ? "outline" : "default";
                        })()}
                        onClick={() => setCustomStartDatePickerOpen(true)}
                      >
                        Custom
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto flex flex-col gap-2">
                      <Calendar
                        mode="single"
                        selected={filterStartDate ?? undefined}
                        onSelect={(date) => {
                          if (date) {
                            const newDate = new Date(date);
                            if (filterStartDate) {
                              newDate.setHours(filterStartDate.getHours());
                              newDate.setMinutes(filterStartDate.getMinutes());
                              newDate.setSeconds(0);
                              newDate.setMilliseconds(0);
                            }
                            updateFilterStartDate(newDate);
                          }
                        }}
                        autoFocus
                      />
                      <input
                        type="time"
                        value={
                          filterStartDate
                            ? `${filterStartDate.getHours().toString().padStart(2, "0")}:${filterStartDate
                                .getMinutes()
                                .toString()
                                .padStart(2, "0")}`
                            : "00:00"
                        }
                        onChange={(e) => {
                          if (filterStartDate) {
                            const [hours, minutes] = e.target.value.split(":").map(Number);
                            const newDate = new Date(filterStartDate);
                            newDate.setHours(hours);
                            newDate.setMinutes(minutes);
                            newDate.setSeconds(0);
                            newDate.setMilliseconds(0);
                            updateFilterStartDate(newDate);
                          }
                        }}
                        className="border rounded px-2 py-1"
                        disabled={!filterStartDate}
                      />
                      <Button onClick={() => setCustomStartDatePickerOpen(false)} disabled={!filterStartDate}>
                        Apply
                      </Button>
                    </PopoverContent>
                  </Popover>
                  {filterStartDate && (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        updateFilterStartDate(null);
                        setCustomStartDatePickerOpen(false);
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <div className="flex w-full justify-end gap-2">
                <Button variant="outline" onClick={() => setIsFilterDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Run No.</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Patient/Encounter</TableHead>
                <TableHead>Test Stages</TableHead>
                <TableHead>Start Time</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : (filteredRuns?.length ?? 0) === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center">
                    {showOnlySuccessful ? "No runs found for this time period" : "No runs found"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRuns?.map((run) => {
                  const runDate = (run.run_start_time ?? "").slice(0, 10);
                  const runNumber = getRunNumberForDate(runs ?? [], run);
                  const wasAnyStageFlaggedByAI =
                    run.endoscopyResult?.result?.aiFlagForReview || run.histologyResult?.result?.aiFlagForReview;
                  const wasFlaggedAsFollowUp =
                    run.endoscopyResult?.result?.followUpStepResult?.isFollowUp ||
                    run.histologyResult?.result?.followUpStepResult?.isFollowUp;

                  return (
                    <TableRow key={run.id}>
                      <TableCell className="font-medium">{`${runDate}_${runNumber}`}</TableCell>
                      <TableCell>
                        {llmModels?.find((model) => model.modelId === run.llm_model_id)?.name ?? "Unknown"}
                      </TableCell>
                      <TableCell>
                        {run.encounter?.patient_encounters?.patient?.id ?? "N"}
                        {"/"}
                        {getPatientEncounterLabel(run.encounter?.patient_encounters)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {run.endoscopyResult ? (
                            <span
                              className={`inline-flex w-fit px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-800`}
                            >
                              {run.endoscopyResult.testData.test_type || "Unknown"}
                              {" - "}
                              {run.endoscopyResult.testData.label} ({run.endoscopyResult.testData.sequence_number})
                            </span>
                          ) : null}
                          {run.histologyResult ? (
                            <span
                              className={`inline-flex w-fit px-2 py-1 rounded-full text-sm bg-green-100 text-green-800`}
                            >
                              {run.histologyResult.testData.test_type || "Unknown"} {run.histologyResult.testData.label}{" "}
                              ({run.histologyResult.testData.sequence_number})
                            </span>
                          ) : null}
                          {run.gpSummaryResult ? (
                            <span
                              className={`inline-flex w-fit px-2 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800`}
                            >
                              {run.gpSummaryResult.testData.test_type || "Unknown"} {run.gpSummaryResult.testData.label}{" "}
                              ({run.gpSummaryResult.testData.sequence_number})
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        {run.run_start_time ? dayjs(run.run_start_time).tz().format("DD/MM/YY HH:mm:ss") : "-"}
                      </TableCell>
                      <TableCell>
                        {run.run_end_time && run.run_start_time ? (
                          (() => {
                            const start = new Date(run.run_start_time);
                            const end = new Date(run.run_end_time);
                            const diffMs = end.getTime() - start.getTime();
                            const diffSec = Math.floor(diffMs / 1000);
                            const min = Math.floor(diffSec / 60);
                            const sec = diffSec % 60;
                            if (min > 0) {
                              return `${min}m ${sec}s`;
                            } else {
                              return `${sec}s`;
                            }
                          })()
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          {run.success !== null ? (
                            run.success ? (
                              <CheckCircle2 className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-500" />
                            )
                          ) : (
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                          )}
                          {/* Robot emoji for AI flag for review */}
                          {wasAnyStageFlaggedByAI ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="ml-2 cursor-help">🤖</span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Flagged by AI for review</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : null}
                          {wasFlaggedAsFollowUp ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="ml-2 cursor-help">🙅‍♂️</span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Flagged as follow up</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!run.run_end_time}
                          onClick={() => navigate(`/test-data/runs/${run.id}`)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default TestRuns;
